import {EtsyListing} from '@coolcolduk/etsy';
import {extractGDriveFolderIdFromUrl, isGDriveSharedLink} from '@coolcolduk/google';
import {useLocalStorageState} from '@coolcolduk/ui-common-hook';
import {useMessage} from '@coolcolduk/ui-mui';
import {Button, Stack, TextField} from '@mui/material';
import {get} from 'lodash';
import {useState} from 'react';
import {QueueJobContent} from '../../../__generated__/resolvers-types';
import CheckboxTable from '../../../components/checkboxTable/CheckboxTable';
import EtsyListingTemplateSelector from '../../../components/etsy/EtsyListingTemplateSelector';
import {JobListButton} from '../../../components/jobList/JobListButton';
import TextFieldGoogleDriveLink from '../../../components/textFieldGoogleDriveLink/TextFieldGoogleDriveLink';
import useGetGDriveContent from '../../../hook/useGetGDriveContent';
import useSetEtsyBulkCreateGdriveFolder from '../../../hook/useSetEtsyBulkCreateGdriveFolder';
import {FeatureProps} from '../../../types/FeatureObject';
import {errorMap} from '../../../util/error/errorMap';
import messageWhenThrown from '../../../util/error/messageWhenThrown';

export interface EtsyBulkCreateGdriveFolderProps extends FeatureProps {}

export default function EtsyBulkCreateGdriveFolder({selectedAccount}: EtsyBulkCreateGdriveFolderProps) {
  const [sharedLink, setSharedLink] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [imageFolder, setImageFolder] = useLocalStorageState<string>(
    `${QueueJobContent.etsyBulkCreateGdriveFolder}-imageFolder`,
    'display',
  );
  const [productFolder, setProductFolder] = useLocalStorageState<string>(
    `${QueueJobContent.etsyBulkCreateGdriveFolder}-productFolder`,
    'product',
  );
  const {showMessage} = useMessage();

  const parentFolderId = isGDriveSharedLink(sharedLink) ? extractGDriveFolderIdFromUrl(sharedLink) : undefined;
  const gdriveData = useGetGDriveContent(parentFolderId);
  const contentList = gdriveData?.children || [];
  const {createBulkEtsyCreateGdriveFolderJob} = useSetEtsyBulkCreateGdriveFolder();
  const [templateListing, setTemplateListing] = useState<EtsyListing | undefined>();

  // show a list of existing job

  return (
    <Stack spacing={2}>
      <JobListButton content={QueueJobContent.etsyBulkCreateGdriveFolder} />

      <EtsyListingTemplateSelector
        selectedAccount={selectedAccount}
        selectedListing={templateListing}
        setSelectedListing={setTemplateListing}
      />

      <TextFieldGoogleDriveLink sharedLink={sharedLink} setSharedLink={setSharedLink} />

      <CheckboxTable
        items={contentList.map((d) => d.name)}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />

      <TextField
        fullWidth
        label="Folder containing your listing images"
        value={imageFolder}
        onChange={(e) => setImageFolder(e.target.value)}
      />

      <TextField
        fullWidth
        label="Folder containing your digital product"
        value={productFolder}
        onChange={(e) => setProductFolder(e.target.value)}
        helperText="Change to something not exists if it is not digital product"
      />

      <Button
        variant="contained"
        color="primary"
        onClick={async () => {
          if (!templateListing) {
            showMessage('Please select a template', undefined, 'warning');
            return;
          }

          const result = await messageWhenThrown(() =>
            createBulkEtsyCreateGdriveFolderJob(
              selectedAccount.id,
              sharedLink,
              JSON.stringify(templateListing),
              imageFolder,
              productFolder,
              contentList.filter((i) => selectedItems.includes(i.name)).map((i) => ({gdriveId: i.id, name: i.name})),
            ),
          );

          showMessage(
            result ? get(errorMap, result, `Job added failed`) : 'Upload job added',
            undefined,
            result ? 'error' : 'success',
          );
          setSelectedItems([]);
        }}
        disabled={!isGDriveSharedLink(sharedLink) || selectedItems.length === 0 || !templateListing}
      >
        Add job
      </Button>
    </Stack>
  );
}
